<template>
    <div>
        <vue3-snackbar top center success="#BD976E" :duration="2000"></vue3-snackbar>

        <!-- Page Header -->
        <div class="page-header">
            <div>
                <h3 class="text-xl font-semibold text-theme-secondary leading-none">Your Orders</h3>
                <!-- <p class="page-subtitle">
                    {{ orders.total_count || orders.data?.total || filteredOrders.length }} of
                    {{ orders.total_count || orders.data?.total || filteredOrders.length }} orders · press
                    <kbd class="kbd">/</kbd> to search
                </p> -->
            </div>
            <div v-if="orders.company_name || accountName" class="account-chip">
                Account: <span class="account-name">{{ orders.company_name || accountName }}</span>
                <template v-if="orders.location_count"> · {{ orders.location_count }} locations</template>
            </div>
        </div>

        <!-- Balance Due Card -->
        <div v-if="showBalanceCard" class="balance-card">
            <div class="balance-card-inner">

                <!-- Left: amount block -->
                <div class="balance-left">
                    <div class="balance-header-row">
                        <span class="balance-label">BALANCE DUE</span>
                        <DropdownMenuRoot>
                            <DropdownMenuTrigger class="balance-location-btn">
                                <LocationMarkerIcon class="h-3 w-3" />
                                <span class="balance-location-label">{{ selectedBalanceLocationLabel }}</span>
                                <ChevronDownIcon class="h-3 w-3" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" :side-offset="6" class="bal-loc-dropdown">
                                <div class="bal-loc-scroll">
                                    <!-- All locations row -->
                                    <DropdownMenuCheckboxItem :checked="selectedBalanceLocation === 'all'"
                                        class="bal-loc-item" @select.prevent="selectedBalanceLocation = 'all'">
                                        <span class="bal-loc-main">
                                            <span class="bal-loc-name">All locations</span>
                                        </span>
                                        <span class="bal-loc-right">
                                            <span class="bal-loc-amount">
                                                ${{ commaFormat(orders.balance_total || 0) }}
                                            </span>
                                            <DropdownMenuItemIndicator class="bal-loc-check">
                                                <CheckIcon class="h-3.5 w-3.5" />
                                            </DropdownMenuItemIndicator>
                                        </span>
                                    </DropdownMenuCheckboxItem>

                                    <!-- Section header -->
                                    <div v-if="orders.locations_with_balance && orders.locations_with_balance.length"
                                        class="bal-loc-section-header">
                                        LOCATIONS WITH BALANCE ({{ orders.locations_with_balance.length }})
                                    </div>

                                    <!-- Per-location rows -->
                                    <DropdownMenuCheckboxItem v-for="loc in orders.locations_with_balance" :key="loc.id"
                                        :checked="selectedBalanceLocation === loc.id" class="bal-loc-item"
                                        @select.prevent="selectedBalanceLocation = loc.id">
                                        <span class="bal-loc-main">
                                            <span class="bal-loc-name">{{ loc.location }}</span>
                                            <span v-if="loc.overdue_amount && Number(loc.overdue_amount) > 0"
                                                class="bal-loc-overdue">
                                                ${{ commaFormat(loc.overdue_amount) }} overdue
                                            </span>
                                        </span>
                                        <span class="bal-loc-right">
                                            <span class="bal-loc-amount">${{ commaFormat(loc.balance) }}</span>
                                            <DropdownMenuItemIndicator class="bal-loc-check">
                                                <CheckIcon class="h-3.5 w-3.5" />
                                            </DropdownMenuItemIndicator>
                                        </span>
                                    </DropdownMenuCheckboxItem>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenuRoot>
                    </div>
                    <p class="balance-amount">${{ commaFormat(currentBalanceTotal) }}</p>
                    <p class="balance-subtext">
                        across <strong>{{ orders.open_invoices_count }}</strong> open invoices
                        <span v-if="currentOverdueAmount" class="overdue-inline">&nbsp;· ${{
                            commaFormat(currentOverdueAmount)
                        }} overdue</span>
                    </p>
                </div>

                <!-- Divider -->
                <div v-if="orders.overdue_count || orders.next_due_date" class="balance-divider"></div>

                <!-- Middle: stats -->
                <div v-if="orders.overdue_count || orders.next_due_date" class="balance-middle">
                    <div v-if="orders.overdue_count" class="balance-stat-group">
                        <span class="balance-stat-label">OVERDUE</span>
                        <span class="balance-stat-value overdue-count">{{ orders.overdue_count }} invoices</span>
                        <span v-if="orders.oldest_overdue_days" class="balance-stat-sub">Oldest {{
                            orders.oldest_overdue_days }}
                            days</span>
                    </div>
                    <div v-if="orders.next_due_date" class="balance-stat-group">
                        <span class="balance-stat-label">NEXT DUE</span>
                        <span class="balance-stat-value balance-next-due">{{ formatDueDate(orders.next_due_date)
                        }}</span>
                        <span v-if="orders.next_due_order_number" class="balance-stat-sub">{{
                            orders.next_due_order_number
                        }}</span>
                    </div>
                </div>

                <!-- Actions pushed to right -->
                <div class="balance-actions">
                    <button class="btn-outline-balance" :disabled="downloadStatementLoader.loading"
                        @click="downloadStatement">
                        <Spinner v-if="downloadStatementLoader.loading" className="mr-2 h-4 w-4" :loading="true" />
                        <DownloadIcon v-else class="mr-2 h-4 w-4" />
                        <span>Statement</span>
                    </button>
                    <button class="btn-pay-balance" @click="selfPay">
                        <span>Pay invoices</span>
                        <ArrowRightIcon class="h-4 w-4" />
                    </button>
                </div>

            </div>
        </div>

        <div class="orders-panel">
            <!-- Filters Section: tabs left, controls right — all one row -->
            <div class="filters-section">
                <div class="header-row">
                    <!-- Status tabs: scrollable row -->
                    <div class="status-tabs-scroll-wrap">
                        <div class="status-tabs-wrap">
                            <button v-for="option in statusFilterOptions" :key="option.value" type="button"
                                class="status-tab" :class="[
                                    'status-tab-' + option.value,
                                    isStatusSelected(option.value) ? 'status-tab-active' : '',
                                ]" @click="toggleStatusOption(option.value)">
                                {{ option.label }}
                                <span class="tab-count">{{ getStatusCount(option.value) }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Filter controls row -->
                    <div class="filter-controls">
                        <!-- Search -->
                        <div class="search-control">
                            <SearchIcon class="search-icon" />
                            <input v-model="search" class="search-input" placeholder="Search orders" type="search"
                                name="search" @keyup="getSearch" />
                            <button v-if="search" type="button" class="search-clear" @click="clearSearch">
                                <XIcon class="h-3.5 w-3.5" />
                            </button>
                        </div>

                        <!-- Date Filter -->
                        <PopoverRoot v-model:open="dateDropdownOpen">
                            <PopoverTrigger class="filter-dropdown-btn filter-dropdown-btn--date"
                                :class="{ 'filter-btn--active': selectedDatePreset !== 'all' }">
                                <CalendarIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span class="filter-btn-label">{{ selectedDateLabel }}</span>
                                <ChevronDownIcon class="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            </PopoverTrigger>
                            <PopoverContent align="start" :side-offset="6" class="date-filter-dropdown"
                                :class="{ 'date-filter-dropdown--cal': showInlinePicker }">
                                <!-- Preset list -->
                                <template v-if="!showInlinePicker">
                                    <div class="loc-dropdown-header">DATE RANGE</div>
                                    <template v-for="option in visibleDatePresetOptions" :key="option.value">
                                        <button class="date-preset-item" :class="{
                                            'date-preset-active': selectedDatePreset === option.value,
                                            'date-preset-custom': option.value === 'custom',
                                        }" @click.stop="selectDatePreset(option.value)">
                                            <span class="date-preset-label">{{ option.label }}</span>
                                            <span v-if="selectedDatePreset === option.value" class="date-preset-check">
                                                <CheckIcon class="h-3.5 w-3.5" />
                                            </span>
                                        </button>
                                        <div v-if="option.value === 'all' && isOldCustomer" class="date-preset-alltime-sub">
                                            Since <strong>Jul 10, 2022.</strong> For earlier invoices, <a
                                                class="date-preset-contact" href="#"
                                                @click.prevent="openContactRep($event)">contact your rep</a>.
                                        </div>
                                    </template>
                                </template>

                                <!-- Calendar picker -->
                                <template v-else>
                                    <div class="cal-picker">
                                        <!-- Two-month grid -->
                                        <div class="cal-months">
                                            <!-- Left month -->
                                            <div class="cal-month">
                                                <div class="cal-month-header">
                                                    <button class="cal-nav-btn"
                                                        @click.stop="prevCalMonth">&#8249;</button>
                                                    <span class="cal-month-label">{{ calLeftLabel }}</span>
                                                    <span></span>
                                                </div>
                                                <div class="cal-grid">
                                                    <span v-for="d in calDow" :key="d" class="cal-dow">{{ d }}</span>
                                                    <button v-for="cell in calLeftGrid" :key="cell.dateStr"
                                                        class="cal-day" :class="calDayClass(cell)"
                                                        :disabled="cell.disabled"
                                                        @click.stop="handleCalClick(cell.dateStr)"
                                                        @mouseenter="calHover = cell.dateStr"
                                                        @mouseleave="calHover = null">
                                                        {{ cell.day }}
                                                    </button>
                                                </div>
                                            </div>
                                            <!-- Right month -->
                                            <div class="cal-month">
                                                <div class="cal-month-header">
                                                    <span></span>
                                                    <span class="cal-month-label">{{ calRightLabel }}</span>
                                                    <button class="cal-nav-btn"
                                                        @click.stop="nextCalMonth">&#8250;</button>
                                                </div>
                                                <div class="cal-grid">
                                                    <span v-for="d in calDow" :key="d" class="cal-dow">{{ d }}</span>
                                                    <button v-for="cell in calRightGrid" :key="cell.dateStr"
                                                        class="cal-day" :class="calDayClass(cell)"
                                                        :disabled="cell.disabled"
                                                        @click.stop="handleCalClick(cell.dateStr)"
                                                        @mouseenter="calHover = cell.dateStr"
                                                        @mouseleave="calHover = null">
                                                        {{ cell.day }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        <!-- Info banner -->
                                        <div class="cal-info-banner">
                                            <span class="cal-info-icon">ⓘ</span>
                                            <span>Orders before <strong>Jul 10, 2022</strong> aren't shown here. For
                                                earlier invoices, <a class="date-preset-contact" href="#"
                                                    @click.prevent="openContactRep($event)">contact your rep</a>.</span>
                                        </div>

                                        <!-- Footer: FROM/TO inputs + actions -->
                                        <div class="cal-footer">
                                            <div class="cal-inputs">
                                                <div class="cal-input-wrap">
                                                    <span class="cal-input-label">FROM</span>
                                                    <span class="cal-input-val">{{ customStartDate || 'YYYY-MM-DD'
                                                    }}</span>
                                                </div>
                                                <span class="cal-input-arrow">&#8594;</span>
                                                <div class="cal-input-wrap">
                                                    <span class="cal-input-label">TO</span>
                                                    <span class="cal-input-val">{{ customEndDate || 'YYYY-MM-DD'
                                                    }}</span>
                                                </div>
                                            </div>
                                            <div class="cal-footer-actions">
                                                <button class="custom-picker-cancel"
                                                    @click.stop="cancelCustomRange">Back</button>
                                                <button class="custom-picker-apply"
                                                    :disabled="!customStartDate || !customEndDate"
                                                    @click.stop="applyCustomRange">Apply</button>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </PopoverContent>
                        </PopoverRoot>

                        <!-- Payment Filter -->
                        <DropdownMenuRoot>
                            <DropdownMenuTrigger class="filter-dropdown-btn filter-dropdown-btn--payment"
                                :class="{ 'filter-btn--active': !isPaymentSelected('all') }">
                                <CreditCardIcon class="h-4 w-4 text-gray-400 flex-shrink-0 filter-btn-mobile-icon" />
                                <span class="filter-btn-label">{{ selectedPaymentLabel }}</span>
                                <ChevronDownIcon class="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" :side-offset="6"
                                class="z-40 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                <div class="loc-dropdown-header">PAYMENT</div>
                                <div class="py-1">
                                    <DropdownMenuCheckboxItem v-for="option in paymentFilterOptions" :key="option.value"
                                        :checked="isPaymentSelected(option.value)"
                                        :disabled="orders.payment_counts && orders.payment_counts[option.value] === 0"
                                        class="flex w-full items-center gap-2 px-3 py-2 text-left filter-dropdown-item hover:bg-gray-50 cursor-pointer"
                                        :class="{ 'filter-item-disabled': orders.payment_counts && orders.payment_counts[option.value] === 0 }"
                                        @select.prevent="togglePaymentOption(option.value)">
                                        <span class="dropdown-checkbox"
                                            :class="{ 'dropdown-checkbox--checked': isPaymentSelected(option.value) }">
                                            <svg v-if="isPaymentSelected(option.value)" viewBox="0 0 10 10" fill="none">
                                                <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" stroke-width="1.6"
                                                    stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </span>
                                        <span v-if="option.dot" class="payment-dot"
                                            :style="{ background: option.dot }"></span>
                                        <span class="flex-1">{{ option.label }}</span>
                                        <span
                                            v-if="orders.payment_counts && orders.payment_counts[option.value] != null"
                                            class="filter-item-count">({{ orders.payment_counts[option.value] }})</span>
                                    </DropdownMenuCheckboxItem>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenuRoot>

                        <!-- Location Filter -->
                        <DropdownMenuRoot v-if="uniqueLocations.length > 1">
                            <DropdownMenuTrigger class="filter-dropdown-btn filter-dropdown-btn--location"
                                :class="{ 'filter-btn--active': !isLocationSelected('all') }">
                                <LocationMarkerIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span class="filter-btn-label">{{ selectedLocationLabel }}</span>
                                <ChevronDownIcon class="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" :side-offset="6"
                                class="z-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 location-dropdown-content">
                                <div class="loc-dropdown-header">LOCATIONS ({{ uniqueLocations.length }})</div>
                                <div class="location-dropdown-scroll py-1">
                                    <DropdownMenuCheckboxItem v-for="loc in uniqueLocations" :key="loc.id"
                                        :checked="isLocationSelected(String(loc.trade_gecko_id))"
                                        :disabled="loc.count === 0"
                                        class="flex w-full items-center gap-2 px-3 py-2 text-left filter-dropdown-item hover:bg-gray-50 cursor-pointer"
                                        :class="{ 'filter-item-disabled': loc.count === 0 }"
                                        @select.prevent="toggleLocationOption(String(loc.trade_gecko_id))">
                                        <span class="dropdown-checkbox"
                                            :class="{ 'dropdown-checkbox--checked': isLocationSelected(String(loc.trade_gecko_id)) }">
                                            <svg v-if="isLocationSelected(String(loc.trade_gecko_id))"
                                                viewBox="0 0 10 10" fill="none">
                                                <path d="M2 5l2.5 2.5L8 3" stroke="currentColor" stroke-width="1.6"
                                                    stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </span>
                                        <span class="flex-1">{{ loc.location }}</span>
                                        <span class="filter-item-count">({{ loc.count }})</span>
                                    </DropdownMenuCheckboxItem>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenuRoot>

                        <!-- Download -->
                        <!-- <Menu as="div" class="relative">
                            <MenuButton class="download-btn">
                                <DownloadIcon class="h-4 w-4" />
                                <span>Download</span>
                                <ChevronDownIcon class="h-3.5 w-3.5" />
                            </MenuButton>
                            <transition enter-active-class="transition duration-100 ease-out"
                                enter-from-class="transform scale-95 opacity-0"
                                enter-to-class="transform scale-100 opacity-100"
                                leave-active-class="transition duration-75 ease-in"
                                leave-from-class="transform scale-100 opacity-100"
                                leave-to-class="transform scale-95 opacity-0">
                                <MenuItems
                                    class="absolute right-0 z-20 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                    <div class="px-1 py-1">
                                        <MenuItem v-slot="{ active }">
                                        <button type="button"
                                            :disabled="!orders.overdue || downloadStatementLoader.loading"
                                            :class="[
                                                active ? 'bg-gray-50 text-theme-secondary' : 'text-gray-700',
                                                'group flex w-full items-center rounded-md px-3 py-2 text-xs',
                                                (!orders.overdue || downloadStatementLoader.loading) && 'cursor-not-allowed opacity-50',
                                            ]" @click="downloadStatement">
                                            <DownloadIcon v-if="!downloadStatementLoader.loading" class="mr-2 h-3.5 w-3.5" />
                                            <Spinner v-else className="mr-2 h-4 w-4" :loading="downloadStatementLoader.loading" />
                                            <span>Download Statement</span>
                                        </button>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                        <button type="button"
                                            :disabled="!selectedInvoiceIds.length || selectedDownloadLoader"
                                            :class="[
                                                active ? 'bg-gray-50 text-theme-secondary' : 'text-gray-700',
                                                'group flex w-full items-center rounded-md px-3 py-2 text-xs',
                                                (!selectedInvoiceIds.length || selectedDownloadLoader) && 'cursor-not-allowed opacity-50',
                                            ]" @click="downloadSelectedInvoices">
                                            <DownloadIcon v-if="!selectedDownloadLoader" class="mr-2 h-3.5 w-3.5" />
                                            <Spinner v-else className="mr-2 h-4 w-4" :loading="selectedDownloadLoader" />
                                            <span>Download Selected ({{ selectedInvoiceIds.length }})</span>
                                        </button>
                                        </MenuItem>
                                        <MenuItem v-slot="{ active }">
                                        <button type="button" :class="[
                                            active ? 'bg-gray-50 text-theme-secondary' : 'text-gray-700',
                                            'group flex w-full items-center rounded-md px-3 py-2 text-xs',
                                        ]" @click="showDownloadModal = true">
                                            <DownloadIcon class="mr-2 h-3.5 w-3.5" />
                                            <span>Multiple Invoices</span>
                                        </button>
                                        </MenuItem>
                                    </div>
                                </MenuItems>
                            </transition>
                        </Menu> -->
                    </div>
                </div>
            </div>

            <!-- Orders List -->
            <div class="orders-list-wrap">
                <!-- Selection Bar -->
                <div v-if="selectedOrderIds.length" class="selection-bar">
                    <div class="selection-info-wrap">
                        <span class="selection-info">
                            {{ effectiveSelectedCount }} selected<template v-if="selectedTotal"> · Total ${{ selectedTotal }}</template>
                        </span>
                        <button v-if="showSelectAllPrompt" class="select-all-filtered-btn" @click="selectAllFiltered">
                            Select all {{ orders.data?.total }} invoices
                        </button>
                    </div>
                    <div class="selection-actions">
                        <button class="selection-btn" :disabled="selectedDownloadLoader"
                            @click="downloadSelectedInvoices">
                            <DownloadIcon class="h-4 w-4" />
                            <span>Download invoices</span>
                        </button>
                        <!-- <button class="selection-btn" :disabled="downloadStatementLoader.loading" @click="downloadStatement">
                            <DocumentTextIcon class="h-4 w-4" />
                            <span>Statement</span>
                        </button> -->
                        <button class="selection-close" @click="clearSelection">
                            <XIcon class="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div v-if="orders.loading" class="table-card">
                    <div class="table-scroll-wrap">
                        <table class="orders-table">
                            <thead class="hidden md:table-header-group">
                                <tr>
                                    <th class="th-check"></th>
                                    <th>ORDER</th>
                                    <th>DATE</th>
                                    <th>LOCATION</th>
                                    <th>STATUS</th>
                                    <th>PAYMENT</th>
                                    <th class="th-amount">AMOUNT</th>
                                    <th class="th-actions" width="300"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <RowLoader :rows="8" />
                            </tbody>
                        </table>
                    </div>
                </div>

                <div v-else-if="!filteredOrders.length" class="table-card">
                    <div class="orders-empty">
                        <img src="@/assets/images/emptybox.svg" alt="Empty" />
                        <p>You don't have any orders yet.</p>
                        <span>
                            <router-link to="/trending">Click here</router-link>
                            for suggestions we think would do well at your store.
                        </span>
                    </div>
                </div>

                <div v-else class="table-card">
                    <div class="table-scroll-wrap">
                        <table class="orders-table">
                            <thead class="hidden md:table-header-group">
                                <tr>
                                    <th class="th-check">
                                        <input type="checkbox"
                                            class="h-4 w-4 rounded border-gray-300 text-theme-secondary focus:ring-theme-secondary"
                                            :checked="allVisibleSelected" :indeterminate.prop="someVisibleSelected"
                                            @change="toggleSelectAllVisible" />
                                    </th>
                                    <th>ORDER</th>
                                    <th>DATE</th>
                                    <th>LOCATION</th>
                                    <th>STATUS</th>
                                    <th>PAYMENT</th>
                                    <th class="th-amount">AMOUNT</th>
                                    <th class="th-actions"></th>
                                </tr>
                            </thead>
                            <OrdersPaymentRow v-for="order in filteredOrders" :key="order.id" :order="order"
                                :is-selected="selectedOrderIds.includes(order.id)" :company-id="orders.company_id"
                                @toggle-select="toggleSelectOrder" />
                        </table>
                    </div> <!-- end table-scroll-wrap -->

                    <!-- Table footer -->
                    <div class="table-footer">
                        <div class="table-footer-left">
                            <span class="showing-text">
                                Showing {{ showingFrom }}-{{ showingTo }} of {{ orders.data?.total || filteredOrders.length }}
                            </span>
                            <div v-if="showPerPageSelector" class="per-page-wrap">
                                <span class="per-page-label">Orders per page</span>
                                <select class="per-page-select" v-model="perPage">
                                    <option v-for="n in visiblePerPageOptions" :key="n" :value="n">{{ n }}</option>
                                </select>
                            </div>
                        </div>
                        <Pagination :total="orders.data.last_page" :active="activePage" @paginate="changeActivePage" />
                    </div>
                </div>
            </div>
        </div>

        <ModalFull :is-visible="showDownloadModal" :reset-container="true" container-class="w-full max-w-2xl"
            @close="showDownloadModal = false">
            <DownloadInvoice @close="closeModal" />
        </ModalFull>
    </div>
</template>

<script>
import Mymixin from "@/modules/mixins";
import { ChevronDownIcon, CheckIcon } from "@heroicons/vue/solid";
import { SearchIcon, DownloadIcon, XIcon, LocationMarkerIcon, CalendarIcon, CreditCardIcon, ArrowRightIcon } from "@heroicons/vue/outline";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import Account from "@/modules/Account";
import { useSnackbar } from "vue3-snackbar";
import Pagination from "./Pagination.vue";
import { ref, reactive, computed, inject, watch } from "vue";
import { useRoute } from "vue-router";
import debounce from "lodash/debounce";
import RowLoader from "@/components/loaders/OrdersRow.vue";
import OrdersPaymentRow from "./OrdersPaymentRow.vue";
import DownloadInvoice from "./DownloadInvoices.vue";
import {
    DropdownMenuRoot,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuCheckboxItem,
    DropdownMenuItemIndicator,
    PopoverRoot,
    PopoverTrigger,
    PopoverContent,
} from "radix-vue";

export default {
    name: "Orders",
    mixins: [Mymixin],
    components: {
        Pagination,
        SearchIcon,
        ChevronDownIcon,
        CheckIcon,
        RowLoader,
        OrdersPaymentRow,
        DownloadIcon,
        DownloadInvoice,
        XIcon,
        LocationMarkerIcon,
        CalendarIcon,
        CreditCardIcon,
        ArrowRightIcon,
        // DocumentTextIcon,
        // Menu,
        // MenuButton,
        // MenuItems,
        // MenuItem,
        DropdownMenuRoot,
        DropdownMenuTrigger,
        DropdownMenuContent,
        DropdownMenuCheckboxItem,
        DropdownMenuItemIndicator,
        PopoverRoot,
        PopoverTrigger,
        PopoverContent,
    },
    methods: {
        moment(date) {
            return moment(date);
        },
        commaFormat(val) {
            return Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        closeModal(length) {
            const text =
                "Please check your email to download the " +
                (length > 1 ? "invoices." : "invoice.");
            if (length) {
                this.$snackbar.add({ type: "success", text, dismissible: false });
            }
            this.showDownloadModal = false;
        },
        formatDueDate(dateStr) {
            return moment(dateStr).format("MMM D, YYYY");
        },
    },
    setup() {
        const { orders, getOrders, downloadStatement, downloadStatementLoader } = Account();
        const axios = inject("axios");
        const route = useRoute();
        const snackbar = useSnackbar();

        // ── Read initial state from URL ──────────────────────────────
        const qp = route.query;
        const parseMulti = (val, fallback) => {
            if (!val) return fallback;
            const arr = String(val).split(",").filter(Boolean);
            return arr.length ? arr : fallback;
        };

        const activePage = ref(Number(qp.page) || 1);
        const perPage = ref(Number(qp.per_page) || 20);
        const PER_PAGE_OPTIONS = [20, 50, 100];
        const search = ref(qp.q || "");
        const statusFilter = ref(parseMulti(qp.status, ["all"]));
        const paymentFilter = ref(parseMulti(qp.payment, ["all"]));
        const selectedLocation = ref(parseMulti(qp.location, ["all"]));
        const selectedDatePreset = ref(qp.date || "all");
        const showDownloadModal = ref(false);
        const selectedOrderIds = ref([]);
        const selectedOrdersMetaMap = reactive({}); // id -> {invoice_id, total} — persists across pages
        const selectAllMode = ref(false); // true = all orders in current filter are selected
        const selectedDownloadLoader = ref(false);
        const selectedBalanceLocation = ref("all");
        const customStartDate = ref(qp.from || "");
        const customEndDate = ref(qp.to || "");
        const todayStr = new Date().toISOString().split("T")[0];
        const showInlinePicker = ref(false);
        const dateDropdownOpen = ref(false);

        watch(dateDropdownOpen, (open) => {
            if (!open && selectedDatePreset.value !== "custom") {
                showInlinePicker.value = false;
            }
        });

        const accountName = localStorage.getItem("company_name") || localStorage.getItem("user_company") || "";

        const statusFilterOptions = [
            { label: "All", value: "all" },
            { label: "Finalized", value: "finalized" },
            { label: "Fulfilled", value: "fulfilled" },
            { label: "Void", value: "void" },
        ];

        const paymentFilterOptions = [
            { label: "Paid", value: "paid", dot: "#16a34a" },
            { label: "Partial", value: "partial", dot: "#d97706" },
            { label: "Unpaid", value: "unpaid", dot: "#dc2626" },
        ];

        const datePresetOptions = [
            { label: "Last 7 days", value: "7d", minHistoryDays: 7 },
            { label: "Last 30 days", value: "30d", minHistoryDays: 30 },
            { label: "Year to date", value: "ytd" },
            { label: "Last year", value: "lastyear" },
            { label: "All time", value: "all" },
            { label: "Custom range...", value: "custom" },
        ];

        // ── Calendar state ───────────────────────────────────────────
        const CAL_MIN = "2022-07-10";
        const calDow = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
        const calYear = ref(new Date().getFullYear());
        const calMonth = ref(new Date().getMonth()); // 0-indexed
        const calHover = ref(null);

        const calRightYear = computed(() => calMonth.value === 11 ? calYear.value + 1 : calYear.value);
        const calRightMonth = computed(() => calMonth.value === 11 ? 0 : calMonth.value + 1);

        const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const calLeftLabel = computed(() => `${MONTH_NAMES[calMonth.value]} ${calYear.value}`);
        const calRightLabel = computed(() => `${MONTH_NAMES[calRightMonth.value]} ${calRightYear.value}`);

        const buildMonthGrid = (year, month) => {
            const firstDow = new Date(year, month, 1).getDay();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const prevDays = new Date(year, month, 0).getDate();
            const cells = [];
            const fmt = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            for (let i = firstDow - 1; i >= 0; i--) {
                const pm = month === 0 ? 11 : month - 1;
                const py = month === 0 ? year - 1 : year;
                cells.push({ day: prevDays - i, dateStr: fmt(py, pm, prevDays - i), outside: true, disabled: true });
            }
            for (let d = 1; d <= daysInMonth; d++) {
                const ds = fmt(year, month, d);
                cells.push({ day: d, dateStr: ds, outside: false, disabled: ds < CAL_MIN });
            }
            const rem = 42 - cells.length;
            for (let d = 1; d <= rem; d++) {
                const nm = month === 11 ? 0 : month + 1;
                const ny = month === 11 ? year + 1 : year;
                cells.push({ day: d, dateStr: fmt(ny, nm, d), outside: true, disabled: true });
            }
            return cells;
        };

        const calLeftGrid = computed(() => buildMonthGrid(calYear.value, calMonth.value));
        const calRightGrid = computed(() => buildMonthGrid(calRightYear.value, calRightMonth.value));

        const prevCalMonth = () => {
            if (calMonth.value === 0) { calYear.value--; calMonth.value = 11; }
            else calMonth.value--;
        };
        const nextCalMonth = () => {
            if (calMonth.value === 11) { calYear.value++; calMonth.value = 0; }
            else calMonth.value++;
        };

        const calDayClass = (cell) => {
            const ds = cell.dateStr;
            const start = customStartDate.value;
            const end = customEndDate.value || (start && !customEndDate.value ? calHover.value : null);
            const rangeStart = start && end ? (start <= end ? start : end) : start;
            const rangeEnd = start && end ? (start <= end ? end : start) : null;
            return {
                "cal-day--outside": cell.outside,
                "cal-day--disabled": cell.disabled,
                "cal-day--start": ds === start && !cell.outside,
                "cal-day--end": ds === (customEndDate.value || null) && !cell.outside,
                "cal-day--in-range": !cell.outside && rangeStart && rangeEnd && ds > rangeStart && ds < rangeEnd,
                "cal-day--hover-end": !cell.outside && !cell.disabled && ds === calHover.value && start && !customEndDate.value,
                "cal-day--today": ds === todayStr,
            };
        };

        const handleCalClick = (dateStr) => {
            if (dateStr < CAL_MIN) return;
            if (!customStartDate.value || (customStartDate.value && customEndDate.value)) {
                customStartDate.value = dateStr;
                customEndDate.value = "";
            } else {
                if (dateStr < customStartDate.value) {
                    customEndDate.value = customStartDate.value;
                    customStartDate.value = dateStr;
                } else {
                    customEndDate.value = dateStr;
                }
            }
        };

        const statusFilterValues = statusFilterOptions
            .filter((o) => o.value !== "all")
            .map((o) => o.value);

        const paymentFilterValues = paymentFilterOptions
            .filter((o) => o.value !== "all")
            .map((o) => o.value);

        const normalizeStatusFilter = (v) => {
            if (Array.isArray(v)) return v;
            if (typeof v === "string" && v) return [v];
            return ["all"];
        };

        const normalizePaymentFilter = (v) => {
            if (Array.isArray(v)) return v;
            if (typeof v === "string" && v) return [v];
            return ["all"];
        };

        const isStatusSelected = (value) => normalizeStatusFilter(statusFilter.value).includes(value);
        const isPaymentSelected = (value) => normalizePaymentFilter(paymentFilter.value).includes(value);
        const isLocationSelected = (value) => Array.isArray(selectedLocation.value)
            ? selectedLocation.value.includes(value)
            : selectedLocation.value === value;

        const selectedPaymentLabel = computed(() => {
            const selected = normalizePaymentFilter(paymentFilter.value).filter((v) => v !== "all");
            if (!selected.length) return "Payment: Any";
            if (selected.length === 1) {
                const found = paymentFilterOptions.find((o) => o.value === selected[0]);
                return found ? `Payment: ${found.label}` : "Payment: Any";
            }
            return `Payment: ${selected.length} selected`;
        });

        const selectedLocationLabel = computed(() => {
            const locs = Array.isArray(selectedLocation.value) ? selectedLocation.value : [selectedLocation.value];
            if (locs.includes("all") || !locs.length) return "All locations";
            if (locs.length === 1) {
                const found = (orders.locations || []).find((l) => String(l.trade_gecko_id) === locs[0]);
                return found ? found.location : "All locations";
            }
            return `${locs.length} locations`;
        });

        const selectedBalanceLocationLabel = computed(() => {
            if (selectedBalanceLocation.value === "all") return "All locations";
            const found = (orders.locations_with_balance || []).find((l) => l.id === selectedBalanceLocation.value);
            return found ? found.location : "All locations";
        });

        const currentOverdueAmount = computed(() => {
            if (selectedBalanceLocation.value === "all") return orders.overdue_amount;
            const found = (orders.locations_with_balance || []).find((l) => l.id === selectedBalanceLocation.value);
            return found ? found.overdue_amount : null;
        });

        const selectedDateLabel = computed(() => {
            if (selectedDatePreset.value === "custom" && customStartDate.value && customEndDate.value) {
                return moment(customStartDate.value).format("MMM D") + " – " + moment(customEndDate.value).format("MMM D, YYYY");
            }
            const found = datePresetOptions.find((o) => o.value === selectedDatePreset.value);
            return found ? found.label : "All time";
        });

        const toggleStatusOption = (value) => {
            statusFilter.value = [value];
        };

        const togglePaymentOption = (value) => {
            if (value === "all") {
                paymentFilter.value = ["all"];
                return;
            }
            const current = normalizePaymentFilter(paymentFilter.value).filter((v) => v !== "all");
            const idx = current.indexOf(value);
            if (idx === -1) current.push(value);
            else current.splice(idx, 1);
            paymentFilter.value = current.length ? current : ["all"];
        };

        const toggleLocationOption = (value) => {
            if (value === "all") {
                selectedLocation.value = ["all"];
                return;
            }
            const current = (Array.isArray(selectedLocation.value) ? selectedLocation.value : [selectedLocation.value])
                .filter((v) => v !== "all");
            const idx = current.indexOf(value);
            if (idx === -1) current.push(value);
            else current.splice(idx, 1);
            selectedLocation.value = current.length ? current : ["all"];
        };

        const selectDatePreset = (value) => {
            if (value === "custom") {
                calYear.value = new Date().getFullYear();
                calMonth.value = new Date().getMonth();
                showInlinePicker.value = true;
                return;
            }
            selectedDatePreset.value = value;
            customStartDate.value = "";
            customEndDate.value = "";
            showInlinePicker.value = false;
            dateDropdownOpen.value = false;
            refetch(1);
        };

        const applyCustomRange = () => {
            if (customStartDate.value && customEndDate.value) {
                selectedDatePreset.value = "custom";
                showInlinePicker.value = false;
                dateDropdownOpen.value = false;
                refetch(1);
            }
        };

        const cancelCustomRange = () => {
            showInlinePicker.value = false;
            if (selectedDatePreset.value !== "custom") {
                customStartDate.value = "";
                customEndDate.value = "";
            }
        };

        const getDateRangeForPreset = (preset) => {
            const now = moment();
            switch (preset) {
                case "today": return [now.clone().startOf("day"), now.clone().endOf("day")];
                case "7d": return [now.clone().subtract(7, "days").startOf("day"), now.clone().endOf("day")];
                case "30d": return [now.clone().subtract(30, "days").startOf("day"), now.clone().endOf("day")];
                case "90d": return [now.clone().subtract(90, "days").startOf("day"), now.clone().endOf("day")];
                case "ytd": return [now.clone().startOf("year"), now.clone().endOf("day")];
                case "lastyear": return [now.clone().subtract(1, "year").startOf("year"), now.clone().subtract(1, "year").endOf("year")];
                case "custom": return (customStartDate.value && customEndDate.value)
                    ? [moment(customStartDate.value).startOf("day"), moment(customEndDate.value).endOf("day")]
                    : null;
                default: return null;
            }
        };

        const getFiltersForRequest = () => {
            const filters = {};
            const status = normalizeStatusFilter(statusFilter.value).find((v) => v !== "all");
            if (status) filters.status = status;

            const payments = normalizePaymentFilter(paymentFilter.value).filter((v) => v !== "all");
            if (payments.length === 1) filters.payment = payments[0];
            else if (payments.length > 1) filters.payment = payments.join(",");

            const locs = (Array.isArray(selectedLocation.value) ? selectedLocation.value : [selectedLocation.value])
                .filter((v) => v !== "all");
            if (locs.length === 1) filters.location = locs[0];
            else if (locs.length > 1) filters.location = locs.join(",");

            if (selectedBalanceLocation.value && selectedBalanceLocation.value !== "all") {
                const balLoc = (orders.locations_with_balance || []).find((l) => l.id === selectedBalanceLocation.value);
                if (balLoc) filters.b_location = balLoc.trade_gecko_id;
            }

            if (search.value) filters.q = search.value;
            filters.per_page = perPage.value;

            const dateRange = getDateRangeForPreset(selectedDatePreset.value);
            if (dateRange) {
                filters.date_from = dateRange[0].format("YYYY-MM-DD");
                filters.date_to = dateRange[1].format("YYYY-MM-DD");
            }

            return filters;
        };

        const filteredOrders = computed(() => orders?.data?.data || []);

        const uniqueLocations = computed(() => orders.locations || []);

        const getStatusCount = (value) => {
            if (value === "all") {
                return orders.total_count || orders.data?.total || (orders?.data?.data || []).length || "";
            }
            if (orders.status_counts && orders.status_counts[value] != null) {
                return orders.status_counts[value];
            }
            const list = orders?.data?.data || [];
            return list.filter((o) => String(o?.status || o?.order_status || "").toLowerCase() === value).length || "";
        };

        const selectedInvoiceIds = computed(() => {
            if (selectAllMode.value) return null;
            return selectedOrderIds.value
                .map((id) => selectedOrdersMetaMap[id]?.invoice_id)
                .filter(Boolean);
        });

        const selectedTotal = computed(() => {
            if (selectAllMode.value) return null;
            const total = selectedOrderIds.value.reduce((sum, id) => {
                return sum + Number(selectedOrdersMetaMap[id]?.total || 0);
            }, 0);
            return total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        });

        const effectiveSelectedCount = computed(() =>
            selectAllMode.value ? (orders.data?.total || 0) : selectedOrderIds.value.length
        );

        const allVisibleSelected = computed(() => {
            if (!filteredOrders.value.length) return false;
            if (selectAllMode.value) return true;
            return filteredOrders.value.every((o) => selectedOrderIds.value.includes(o.id));
        });

        const someVisibleSelected = computed(() => {
            if (!filteredOrders.value.length) return false;
            if (selectAllMode.value) return false;
            const count = filteredOrders.value.filter((o) => selectedOrderIds.value.includes(o.id)).length;
            return count > 0 && count < filteredOrders.value.length;
        });

        const showSelectAllPrompt = computed(() =>
            !selectAllMode.value &&
            allVisibleSelected.value &&
            (orders.data?.total || 0) > filteredOrders.value.length
        );

        const showBalanceCard = computed(() => {
            return orders.overdue || Number(orders.balance_total) > 0;
        });

        const currentBalanceTotal = computed(() => {
            if (selectedBalanceLocation.value === "all") return orders.balance_total || 0;
            const found = (orders.locations_with_balance || []).find((l) => l.id === selectedBalanceLocation.value);
            return found ? found.balance : 0;
        });

        const showingFrom = computed(() => orders.data?.from || 1);
        const showingTo = computed(() => orders.data?.to || filteredOrders.value.length);
        const ordersTotal = computed(() => Number(orders.data?.total || filteredOrders.value.length || 0));
        // Only offer page sizes that actually paginate the current filtered list.
        // e.g. 1–19 items: hide the dropdown; 20–49: 20; 50–99: 20/50; 100+: 20/50/100.
        const visiblePerPageOptions = computed(() => PER_PAGE_OPTIONS.filter((n) => n < ordersTotal.value));
        const showPerPageSelector = computed(() => visiblePerPageOptions.value.length > 0);
        const isOldCustomer = computed(() => {
            if (!orders.customer_since) return false;
            return new Date(orders.customer_since) < new Date('2022-07-10');
        });
        // Hide date presets that extend beyond available invoice history.
        // History start is customer_since, clamped to CAL_MIN (invoices before that aren't listed).
        // If customer_since is unknown, show every preset rather than guessing.
        const visibleDatePresetOptions = computed(() => {
            if (!orders.customer_since) return datePresetOptions;
            const historyStart = moment.max(moment(orders.customer_since), moment(CAL_MIN)).startOf("day");
            const now = moment();
            const historyDays = now.diff(historyStart, "days");
            const ytdStart = now.clone().startOf("year");
            const lastYearEnd = now.clone().subtract(1, "year").endOf("year");
            return datePresetOptions.filter((option) => {
                if (option.minHistoryDays != null) return historyDays > option.minHistoryDays;
                if (option.value === "ytd") return historyStart.isBefore(ytdStart);
                if (option.value === "lastyear") return historyStart.isSameOrBefore(lastYearEnd);
                return true;
            });
        });

        const refetch = (page = 1) => {
            activePage.value = page;
            selectedOrderIds.value = [];
            Object.keys(selectedOrdersMetaMap).forEach((k) => delete selectedOrdersMetaMap[k]);
            selectAllMode.value = false;
            getOrders(page, getFiltersForRequest());
        };

        // ── Sync filter state → URL (debounced, replace not push) ────
        const syncUrl = debounce(() => {
            const q = {};
            const status = statusFilter.value.filter((v) => v !== "all");
            if (status.length) q.status = status.join(",");

            const payment = paymentFilter.value.filter((v) => v !== "all");
            if (payment.length) q.payment = payment.join(",");

            const locs = (Array.isArray(selectedLocation.value) ? selectedLocation.value : [selectedLocation.value])
                .filter((v) => v !== "all");
            if (locs.length) q.location = locs.join(",");

            if (selectedBalanceLocation.value && selectedBalanceLocation.value !== "all") {
                const balLoc = (orders.locations_with_balance || []).find((l) => l.id === selectedBalanceLocation.value);
                if (balLoc) q.b_location = balLoc.trade_gecko_id;
            }

            if (selectedDatePreset.value && selectedDatePreset.value !== "all") q.date = selectedDatePreset.value;
            if (customStartDate.value) q.from = customStartDate.value;
            if (customEndDate.value) q.to = customEndDate.value;
            if (search.value) q.q = search.value;
            if (activePage.value > 1) q.page = activePage.value;
            if (perPage.value !== 20) q.per_page = perPage.value;

            const url = new URL(window.location.href);
            url.search = new URLSearchParams(q).toString();
            window.history.replaceState(window.history.state, "", url.toString());
        }, 300);

        watch([statusFilter, paymentFilter, selectedLocation, selectedBalanceLocation, selectedDatePreset, customStartDate, customEndDate, search, activePage, perPage], syncUrl, { deep: true });

        // Initial fetch using restored params
        getOrders(activePage.value, getFiltersForRequest());

        watch(statusFilter, () => refetch(1));
        watch(paymentFilter, () => refetch(1));
        watch(selectedLocation, () => refetch(1));
        watch(selectedBalanceLocation, () => refetch(1));
        watch(perPage, () => refetch(1));
        watch(visiblePerPageOptions, (opts) => {
            if (!opts.length || opts.includes(perPage.value)) return;
            perPage.value = opts[opts.length - 1];
        });
        watch(visibleDatePresetOptions, (opts) => {
            const values = new Set(opts.map((o) => o.value));
            if (selectedDatePreset.value === "custom" || values.has(selectedDatePreset.value)) return;
            selectedDatePreset.value = values.has("all") ? "all" : opts[0]?.value || "all";
            customStartDate.value = "";
            customEndDate.value = "";
            refetch(1);
        });

        const openContactRep = (e) => {
            e.preventDefault();
            document.querySelector("#rep-panel button")?.click();
        };

        const clearSearch = () => {
            search.value = "";
            refetch(1);
        };

        const clearSelection = () => {
            selectedOrderIds.value = [];
            Object.keys(selectedOrdersMetaMap).forEach((k) => delete selectedOrdersMetaMap[k]);
            selectAllMode.value = false;
        };

        const changeActivePage = (page) => {
            if (!page) return;
            // Preserve selectedOrderIds across page navigation
            getOrders(page, getFiltersForRequest());
            activePage.value = page;
            if (window.innerWidth < 768) {
                document.querySelector(".orders-list-wrap")?.scrollIntoView({ behavior: "smooth" });
            }
        };

        const toggleSelectOrder = ({ orderId, checked }) => {
            selectAllMode.value = false;
            if (checked && !selectedOrderIds.value.includes(orderId)) {
                selectedOrderIds.value.push(orderId);
                const o = filteredOrders.value.find((x) => x.id === orderId);
                if (o) selectedOrdersMetaMap[orderId] = { invoice_id: o.invoice_id, total: o.total };
            }
            if (!checked) {
                selectedOrderIds.value = selectedOrderIds.value.filter((id) => id !== orderId);
                delete selectedOrdersMetaMap[orderId];
            }
        };

        const toggleSelectAllVisible = (event) => {
            selectAllMode.value = false;
            const pageIds = new Set(filteredOrders.value.map((o) => o.id));
            if (event.target.checked) {
                const merged = [...new Set([...selectedOrderIds.value, ...pageIds])];
                selectedOrderIds.value = merged;
                filteredOrders.value.forEach((o) => {
                    selectedOrdersMetaMap[o.id] = { invoice_id: o.invoice_id, total: o.total };
                });
            } else {
                selectedOrderIds.value = selectedOrderIds.value.filter((id) => !pageIds.has(id));
                pageIds.forEach((id) => delete selectedOrdersMetaMap[id]);
            }
        };

        const selectAllFiltered = () => {
            selectAllMode.value = true;
            // Also mark current page items as selected so checkboxes show filled
            const merged = [...new Set([...selectedOrderIds.value, ...filteredOrders.value.map((o) => o.id)])];
            selectedOrderIds.value = merged;
            filteredOrders.value.forEach((o) => {
                selectedOrdersMetaMap[o.id] = { invoice_id: o.invoice_id, total: o.total };
            });
        };

        const downloadSelectedInvoices = () => {
            const hasSelection = selectAllMode.value || selectedInvoiceIds.value?.length;
            if (!hasSelection || selectedDownloadLoader.value) return;
            // selectedDownloadLoader.value = true;
            const payload = { 
                self_receiver: true,
                user: localStorage.getItem("user_id"),
                company_id: localStorage.getItem("compnay_tg_id")
            };
            if (selectAllMode.value) {
                payload.select_all = true;
                const filters = getFiltersForRequest();
                if (filters.status) payload.status = filters.status;
                if (filters.payment) payload.payment = filters.payment;
                if (filters.location) payload.location = filters.location;
                if (filters.date_from) payload.date_from = filters.date_from;
                if (filters.date_to) payload.date_to = filters.date_to;
                if (filters.q) payload.q = filters.q;
            } else {
                payload.invoice_ids = selectedInvoiceIds.value;
            }
            axios.api
                .post("/bulk-invoices", payload, { responseType: "blob" })
                .then((response) => {
                    if (response.data) {
                        clearSelection();
                        snackbar.add({
                            type: "success",
                            text: "Please check your email to download the invoices.",
                            dismissible: false,
                        });
                    }
                })
                .finally(() => { selectedDownloadLoader.value = false; });
        };

        const selfPay = () => {
            window.open("/invoice-payments/" + orders.company_id, "_blank");
        };

        const getSearch = debounce(() => refetch(1), 500);

        return {
            orders,
            activePage,
            changeActivePage,
            search,
            statusFilterOptions,
            paymentFilterOptions,
            datePresetOptions,
            selectedDatePreset,
            selectedDateLabel,
            selectedLocation,
            selectedLocationLabel,
            selectedPaymentLabel,
            selectedBalanceLocation,
            selectedBalanceLocationLabel,
            currentBalanceTotal,
            currentOverdueAmount,
            isStatusSelected,
            isPaymentSelected,
            isLocationSelected,
            toggleStatusOption,
            togglePaymentOption,
            toggleLocationOption,
            selectDatePreset,
            filteredOrders,
            uniqueLocations,
            getStatusCount,
            selectedOrderIds,
            selectedInvoiceIds,
            selectedTotal,
            effectiveSelectedCount,
            selectAllMode,
            showSelectAllPrompt,
            selectAllFiltered,
            allVisibleSelected,
            someVisibleSelected,
            showBalanceCard,
            selectedDownloadLoader,
            toggleSelectOrder,
            toggleSelectAllVisible,
            downloadSelectedInvoices,
            showDownloadModal,
            downloadStatement,
            downloadStatementLoader,
            selfPay,
            getSearch,
            clearSearch,
            openContactRep,
            clearSelection,
            perPage,
            visiblePerPageOptions,
            showPerPageSelector,
            showingFrom,
            showingTo,
            visibleDatePresetOptions,
            isOldCustomer,
            accountName,
            customStartDate,
            customEndDate,
            todayStr,
            showInlinePicker,
            dateDropdownOpen,
            applyCustomRange,
            cancelCustomRange,
            calDow,
            calLeftLabel,
            calRightLabel,
            calLeftGrid,
            calRightGrid,
            calHover,
            calDayClass,
            handleCalClick,
            prevCalMonth,
            nextCalMonth,
        };
    },
};
</script>

<style lang="scss">
/* Prevent layout shift when Radix dropdowns open.
   Setting overflow-y:scroll on html keeps the scrollbar always present,
   so Radix calculates 0px compensation. Force padding-right:0 overrides
   any inline style Radix sets on body. */
html {
    overflow-y: scroll;
}

body {
    padding-right: 0 !important;
}

/* Shared dropdown item style — matches date preset font/color */
.filter-dropdown-item {
    font-size: 13px !important;
    color: #374151 !important;
}

/* Balance card location dropdown — global because Radix portals outside component */
.bal-loc-dropdown {
    z-index: 50;
    width: 350px;
    border-radius: 12px;
    background: #ffffff !important;
    box-shadow: 0 10px 26px rgba(43, 48, 63, 0.12), 0 0 0 1px rgba(148, 163, 184, 0.18);
    overflow: hidden;
}

.bal-loc-scroll {
    max-height: 300px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 6px;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 99px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }
}

.bal-loc-section-header {
    padding: 8px 8px 4px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #9ca3af;
}

.bal-loc-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 9px 10px;
    gap: 8px;
    cursor: pointer;
    background: #ffffff;
    border: none;
    border-radius: 8px;
    text-align: left;
    outline: none;
    transition: background 0.12s, border-color 0.12s;

    &:hover,
    &[data-highlighted] {
        background: #f8fafc;
    }

    &[data-state="checked"] {
        background: #f3f4f6;
    }
}

.bal-loc-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.bal-loc-right {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.bal-loc-name {
    font-size: 13px;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bal-loc-overdue {
    color: #b45309;
    font-size: 11px;
    font-weight: 500;
}

.bal-loc-amount {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    flex-shrink: 0;
    white-space: nowrap;
}

.bal-loc-check {
    color: #2b303f;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.12s ease;
}

.bal-loc-item[data-state="checked"] .bal-loc-check {
    opacity: 1;
}

.bal-loc-item:not([data-state="checked"]) .bal-loc-check {
    pointer-events: none;
}

/* Date filter popover — global because Radix portals outside component */
.date-filter-dropdown {
    z-index: 40;
    background: #ffffff !important;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06);
    min-width: 192px;
    padding: 6px 0;
    overflow: hidden;
}

.date-filter-dropdown.date-filter-dropdown--cal {
    padding: 0;
    min-width: 0;
    width: max-content;
}

.date-preset-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    font-size: 13px;
    color: #374151;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: none;
    gap: 12px;
}

.date-preset-item:hover {
    background: #f9fafb;
}

.date-preset-item.date-preset-custom {
    border-top: 1px solid #f1f5f9;
    margin-top: 2px;
    padding-top: 10px;
    color: #374151;
}

.date-preset-item.date-preset-active .date-preset-label {
    font-weight: 600;
    color: #0f172a;
}

.date-preset-check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #374151;
    flex-shrink: 0;
}

.date-preset-alltime-sub {
    padding: 0 14px 10px;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
    margin-top: -4px;
}

.date-preset-contact {
    color: #2563eb;
    text-decoration: underline;
}

.date-preset-contact:hover {
    color: #1d4ed8;
}

.cal-picker {
    width: 620px;
    padding: 20px 20px 0;
}

.cal-months {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
}

.cal-month-header {
    display: grid;
    grid-template-columns: 28px 1fr 28px;
    align-items: center;
    margin-bottom: 10px;
    text-align: center;
}

.cal-month-label {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    text-align: center;
}

.cal-nav-btn {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    font-size: 18px;
    color: #374151;
    cursor: pointer;
    line-height: 1;
}

.cal-nav-btn:hover {
    background: #f3f4f6;
}

.cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
}

.cal-dow {
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.04em;
}

.cal-day {
    position: relative;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #374151;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.1s;
}

.cal-day:hover:not(:disabled):not(.cal-day--start):not(.cal-day--end) {
    background: #f3f4f6;
}

.cal-day.cal-day--outside {
    color: #d1d5db;
    pointer-events: none;
}

.cal-day.cal-day--disabled {
    color: #d1d5db;
    cursor: not-allowed;
    pointer-events: none;
}

.cal-day.cal-day--today {
    font-weight: 700;
    color: #1d4ed8;
}

.cal-day.cal-day--start,
.cal-day.cal-day--end {
    background: #1e293b;
    color: #ffffff;
    border-radius: 6px;
    font-weight: 600;
    z-index: 1;
}

.cal-day.cal-day--hover-end {
    background: #e2e8f0;
    border-radius: 6px;
}

.cal-day.cal-day--in-range {
    background: #f1f5f9;
    border-radius: 0;
    color: #1e293b;
}

.cal-info-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 16px 0 0;
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    font-size: 12.5px;
    color: #78350f;
    line-height: 1.5;
}

.cal-info-banner strong {
    font-weight: 600;
}

.cal-info-icon {
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
}

.cal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0 16px;
    gap: 12px;
    border-top: 1px solid #f1f5f9;
    margin-top: 14px;
}

.cal-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cal-input-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    background: #ffffff;
}

.cal-input-label {
    font-size: 10.5px;
    letter-spacing: 0.06em;
    color: #9ca3af;
}

.cal-input-val {
    font-size: 13px;
    color: #374151;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.cal-input-arrow {
    font-size: 14px;
    color: #9ca3af;
}

.cal-footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.custom-picker-cancel {
    height: 34px;
    padding: 0 14px;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    font-size: 13px;
    color: #374151;
    background: #ffffff;
    cursor: pointer;
}

.custom-picker-cancel:hover {
    background: #f9fafb;
}

.custom-picker-apply {
    height: 34px;
    padding: 0 16px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: #1e293b;
    cursor: pointer;
}

.custom-picker-apply:hover {
    background: #0f172a;
}

.custom-picker-apply:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

@media (max-width: 767px) {
    .date-filter-dropdown {
        max-height: 80vh;
        overflow-y: auto;
    }

    .cal-picker {
        width: 100%;
        padding: 16px 16px 0;
        box-sizing: border-box;
    }

    .cal-months {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .cal-footer {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px 0 14px;
    }

    .cal-inputs {
        justify-content: center;
    }

    .cal-footer-actions {
        justify-content: stretch;
    }

    .cal-footer-actions .custom-picker-cancel,
    .cal-footer-actions .custom-picker-apply {
        flex: 1;
        justify-content: center;
    }
}
</style>

<style lang="scss" scoped>
input[type="search"]::-webkit-search-cancel-button {
    display: none;
}

/* ─── Page header ─────────────────────────────────────────── */
.page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 14px;
}

.page-subtitle {
    margin-top: 4px;
    font-size: 13px;
    color: #94a3b8;
    line-height: 1;

    .kbd {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        background: #f3f4f6;
        padding: 1px 5px;
        font-size: 11px;
        color: #6b7280;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        margin: 0 2px;
        vertical-align: middle;
    }
}

.account-chip {
    font-size: 13px;
    color: #64748b;
    flex-shrink: 0;
    padding-top: 4px;

    .account-name {
        font-weight: 600;
        color: #1e293b;
    }
}

/* ─── Filters card ────────────────────────────────────────── */
.orders-panel {
    background: #ffffff;
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 16px;
}

.filters-section {
    background: transparent;
    border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

/* Single row: tabs left, controls right */
.header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    gap: 12px;
    flex-wrap: wrap;
}

/* ─── Status tabs — segmented control style ───────────────── */
.status-tabs-wrap {
    display: flex;
    align-items: center;
    gap: 0;
    flex-shrink: 0;
    background: #ffffff;
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 12px;
    overflow: hidden;
    padding: 0;
}

.status-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 0;
    border: none;
    border-right: 1px solid rgba(148, 163, 184, 0.2);
    background: #ffffff;
    padding: 7px 14px;
    font-size: 13.5px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    color: #485a5e;
    transition: background 0.12s ease, box-shadow 0.12s ease, color 0.12s ease, border-color 0.12s ease;

    &:first-child {
        border-top-left-radius: 11px;
        border-bottom-left-radius: 11px;
    }

    &:last-child {
        border-right: none;
        border-top-right-radius: 11px;
        border-bottom-right-radius: 11px;
    }

    .tab-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 9999px;
        font-size: 11.5px;
        font-weight: 600;
        line-height: 1;
        padding: 3px 7px;
        min-width: 22px;
        background: rgba(72, 90, 94, 0.1);
        color: #485a5e;
        transition: background 0.12s ease, color 0.12s ease;
    }

    &:hover:not(.status-tab-active) {
        background: #f8fafc;
        color: #2b303f;

        .tab-count {
            background: rgba(148, 163, 184, 0.16);
            color: #475569;
        }
    }
}

/* Active tab — white card floating on gray bg */
.status-tab-active {
    background: #eef2f6;
    color: #2b303f;
    font-weight: 500;

    .tab-count {
        background: rgba(148, 163, 184, 0.22);
        color: #2b303f;
    }
}

/* ─── Right-side filter controls ──────────────────────────── */
.filter-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

/* Search box */
.search-control {
    position: relative;
    display: flex;
    align-items: center;
    width: 210px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    color: #9ca3af;

    &:focus-within {
        border-color: #9ca3af;
        color: #6b7280;
    }
}

.search-icon {
    position: absolute;
    left: 10px;
    width: 15px;
    height: 15px;
    pointer-events: none;
    flex-shrink: 0;
    color: inherit;
}

.search-input {
    width: 100%;
    height: 100%;
    border: none;
    background: transparent;
    padding: 0 28px 0 32px;
    font-size: 13.5px;
    color: #1f2937;

    &::placeholder {
        color: #9ca3af;
    }

    &:focus {
        outline: none;
        box-shadow: none;
    }
}

.search-clear {
    position: absolute;
    right: 8px;
    display: flex;
    align-items: center;
    color: #9ca3af;

    &:hover {
        color: #6b7280;
    }
}

/* Filter dropdown buttons (All time / Payment / All locations) */
.filter-dropdown-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    padding: 0 11px;
    font-size: 13.5px;
    color: #374151;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
    overflow: hidden;
    min-width: 0;
    max-width: 280px;

    &:hover {
        border-color: #d1d5db;
        background: #f9fafb;
    }

    .filter-btn-label {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
        text-align: left;
        font-size: 13px;
    }
}

.filter-dropdown-btn--date {
    width: auto;
    min-width: 188px;
}

.filter-dropdown-btn--payment {
    width: auto;
    min-width: 162px;
}

.filter-dropdown-btn--location {
    width: auto;
    min-width: 176px;
}

/* ─── Date filter dropdown ─────────────────────────────────── */
.date-filter-dropdown {
    z-index: 40;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06);
    min-width: 192px;
    padding: 6px 0;
    overflow: hidden;

    &.date-filter-dropdown--cal {
        padding: 0;
        min-width: 0;
        width: max-content;
    }
}

.date-preset-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 12px;
    font-size: 13px;
    color: #374151;
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: none;
    gap: 12px;

    &:hover {
        background: #f9fafb;
    }

    &.date-preset-custom {
        border-top: 1px solid #f1f5f9;
        margin-top: 2px;
        padding-top: 10px;
        color: #374151;
    }

    &.date-preset-active .date-preset-label {
        font-weight: 600;
        color: #0f172a;
    }
}

.date-preset-check {
    font-size: 13px;
    font-weight: 700;
    color: #374151;
    flex-shrink: 0;
}

.date-preset-alltime-sub {
    padding: 0 14px 10px;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
    margin-top: -4px;
}

.date-preset-contact {
    color: #2563eb;
    text-decoration: underline;

    &:hover {
        color: #1d4ed8;
    }
}

/* ─── Calendar picker ──────────────────────────────────────── */
.cal-picker {
    width: 620px;
    padding: 20px 20px 0;
}

.cal-months {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 28px;
}

.cal-month-header {
    display: grid;
    grid-template-columns: 28px 1fr 28px;
    align-items: center;
    margin-bottom: 10px;
    text-align: center;
}

.cal-month-label {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    text-align: center;
}

.cal-nav-btn {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    font-size: 18px;
    color: #374151;
    cursor: pointer;
    line-height: 1;

    &:hover {
        background: #f3f4f6;
    }
}

.cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
}

.cal-dow {
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.04em;
}

.cal-day {
    position: relative;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    color: #374151;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.1s;

    &:hover:not(:disabled):not(.cal-day--start):not(.cal-day--end) {
        background: #f3f4f6;
    }

    &.cal-day--outside {
        color: #d1d5db;
        pointer-events: none;
    }

    &.cal-day--disabled {
        color: #d1d5db;
        cursor: not-allowed;
        pointer-events: none;
    }

    &.cal-day--today {
        font-weight: 700;
        color: #1d4ed8;
    }

    &.cal-day--start,
    &.cal-day--end {
        background: #1e293b;
        color: #ffffff;
        border-radius: 6px;
        font-weight: 600;
        z-index: 1;
    }

    &.cal-day--hover-end {
        background: #e2e8f0;
        border-radius: 6px;
    }

    &.cal-day--in-range {
        background: #f1f5f9;
        border-radius: 0;
        color: #1e293b;
    }
}

.cal-info-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 16px 0 0;
    padding: 10px 12px;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 8px;
    font-size: 12.5px;
    color: #78350f;
    line-height: 1.5;

    strong {
        font-weight: 600;
    }
}

.cal-info-icon {
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
}

.cal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 0 16px;
    gap: 12px;
    border-top: 1px solid #f1f5f9;
    margin-top: 14px;
}

.cal-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
}

.cal-input-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    background: #ffffff;
}

.cal-input-label {
    font-size: 10.5px;
    letter-spacing: 0.06em;
    color: #9ca3af;
}

.cal-input-val {
    font-size: 13px;
    color: #374151;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.cal-input-arrow {
    font-size: 14px;
    color: #9ca3af;
}

.cal-footer-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.custom-picker-cancel {
    height: 34px;
    padding: 0 14px;
    border: 1px solid #e5e7eb;
    border-radius: 7px;
    font-size: 13px;
    color: #374151;
    background: #ffffff;
    cursor: pointer;

    &:hover {
        background: #f9fafb;
    }
}

.custom-picker-apply {
    height: 34px;
    padding: 0 16px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: #1e293b;
    cursor: pointer;

    &:hover {
        background: #0f172a;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

/* Hide payment icon on desktop — it's a mobile-only affordance */
.filter-btn-mobile-icon {
    display: none;
}

/* Highlight active filter buttons */
.filter-btn--active {
    border-color: #94a3b8;
    background: #f8fafc;

    .filter-btn-label {
        color: #1e293b;
        font-weight: 500;
    }
}

/* Checkbox inside dropdown items */
.dropdown-checkbox {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    border-radius: 4px;
    border: 1.5px solid #d1d5db;
    background: #ffffff;
    transition: border-color 0.1s, background 0.1s;

    svg {
        width: 10px;
        height: 10px;
    }

    &--checked {
        border-color: #1e293b;
        background: #1e293b;
        color: #ffffff;
    }
}

.payment-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.filter-item-count {
    font-size: 11px;
    color: #9ca3af;
    font-variant-numeric: tabular-nums;
    margin-left: auto;
    flex-shrink: 0;
}

.filter-item-disabled {
    opacity: 0.4;
    cursor: not-allowed !important;
    pointer-events: none;
}

/* Download button — slightly gray background */
.download-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    height: 36px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
    padding: 0 13px;
    font-size: 13.5px;
    font-weight: 500;
    color: #374151;
    white-space: nowrap;
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s;

    &:hover {
        border-color: #d1d5db;
        background: #f3f4f6;
    }
}

/* Balance Due Card */
.balance-card {
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    background: #fff8f2;
    border: 1px solid rgba(189, 150, 111, 0.22);
    border-left: 4px solid #bd966f;
}

.balance-card-inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    padding: 16px 20px;
    width: 100%;
    box-sizing: border-box;
}

.balance-left {
    flex-shrink: 0;
    width: 360px;
}

.balance-header-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.balance-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #485a5e;
}

.balance-location-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 9999px;
    padding: 2px 8px 2px 6px;
    font-size: 11.5px;
    color: #2b303f;
    cursor: pointer;
    border: 1px solid rgba(189, 150, 111, 0.32);
    background: rgba(255, 255, 255, 0.9);
    transition: border-color 0.12s;

    &:hover {
        border-color: rgba(189, 150, 111, 0.56);
    }
}

.balance-location-label {
    display: inline-block;
    max-width: 150px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.balance-amount {
    font-size: 30px;
    font-weight: 700;
    color: #2b303f;
    line-height: 1.1;
    margin-bottom: 3px;
    letter-spacing: -0.5px;
}

.balance-subtext {
    font-size: 13px;
    color: #485a5e;

    strong {
        font-weight: 600;
        color: #2b303f;
    }

    .overdue-inline {
        font-weight: 600;
        color: #bd966f;
    }
}

.balance-divider {
    width: 1px;
    height: 52px;
    background: rgba(189, 150, 111, 0.22);
    margin: 0 28px;
    flex-shrink: 0;
}

.balance-middle {
    display: flex;
    gap: 32px;
    flex-shrink: 0;
}

.balance-stat-group {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.balance-stat-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #485a5e;
    margin-bottom: 1px;
}

.balance-stat-value {
    font-size: 14px;
    font-weight: 600;
    color: #2b303f;

    &.overdue-count {
        color: #b45309;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
}

.balance-next-due {
    font-size: 15px;
    font-weight: 700;
    color: #2b303f;
}

.balance-stat-sub {
    font-size: 11.5px;
    color: #485a5e;
    margin-top: 1px;
}

.balance-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    flex-shrink: 0;
}

.btn-outline-balance {
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 16px;
    border-radius: 10px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    font-size: 13.5px;
    font-weight: 500;
    color: #2b303f;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;

    &:hover {
        background: #f3f4f6;
        border-color: #d1d5db;
        color: #2b303f;
    }
}

.btn-pay-balance {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 600;
    color: #ffffff;
    background: #2b303f;
    cursor: pointer;
    transition: background 0.12s, transform 0.12s;

    &:hover {
        background: rgba(43, 48, 63, 0.95);
    }

    &:active {
        transform: translateY(1px);
    }
}

/* Orders list */
.orders-list-wrap {
    @apply space-y-0;
}

/* Selection bar */
.selection-bar {
    @apply flex items-center justify-between gap-4 rounded-t-xl bg-gray-900 px-4 py-3;
}

.selection-info-wrap {
    @apply flex flex-col gap-0.5;
}

.selection-info {
    @apply text-sm font-medium text-white;
}

.select-all-filtered-btn {
    @apply text-xs text-blue-300 underline hover:text-blue-200 transition-colors text-left;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-underline-offset: 2px;
}

.selection-actions {
    @apply flex items-center gap-2;
}

.selection-btn {
    @apply inline-flex items-center gap-1.5 rounded-lg border border-gray-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-gray-800;

    &:disabled {
        opacity: 0.5;
    }
}

.selection-close {
    @apply flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-800 hover:text-white;
}

/* Table card */
.table-card {
    @apply bg-white overflow-hidden;
}

.table-scroll-wrap {
    overflow-x: auto;
}

.orders-table {
    @apply min-w-full divide-y divide-gray-100;

    th {
        @apply bg-white px-4 py-3 text-left text-xxs font-semibold uppercase tracking-wider text-gray-500;
    }
}

.th-check {
    @apply w-12 pl-4 pr-0;
}

.th-amount {
    text-align: right !important;
    width: 120px;
    padding-right: 24px;
}

.th-actions {
    // width: 168px;
}

/* Table footer */
.table-footer {
    @apply flex items-center justify-between border-t border-gray-100 px-4 py-2;
}

.table-footer-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.showing-text {
    @apply text-xs text-theme-body;
}

.per-page-wrap {
    display: flex;
    align-items: center;
    gap: 6px;
}

.per-page-label {
    font-size: 12px;
    color: #9ca3af;
    white-space: nowrap;
}

.per-page-select {
    height: 28px;
    padding: 0 22px 0 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    font-size: 12px;
    color: #374151;
    cursor: pointer;
    appearance: auto;

    &:focus {
        outline: none;
        border-color: #9ca3af;
    }
}

/* Location dropdown header */
.loc-dropdown-header {
    padding: 10px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.07em;
    color: #9ca3af;
}

/* Location dropdown scrollable list */
.location-dropdown-scroll {
    max-height: 260px;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
    scrollbar-color: #d1d5db transparent;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 99px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }
}


/* ─── Mobile responsive ───────────────────────────────────── */
@media (max-width: 767px) {

    /* Page header stacks */
    .page-header {
        flex-direction: column;
        gap: 6px;
        margin-bottom: 10px;
    }

    .account-chip {
        font-size: 12px;
        padding-top: 0;
    }

    /* Filters */
    .header-row {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        padding: 10px 12px;
    }

    .status-tabs-scroll-wrap {
        position: relative;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        /* bleed to card edges, re-add padding inside */
        margin: 0 -12px;
        padding: 0 12px;
        /* right fade to hint more content */
        -webkit-mask-image: linear-gradient(to right, black calc(100% - 32px), transparent 100%);
        mask-image: linear-gradient(to right, black calc(100% - 32px), transparent 100%);

        &::-webkit-scrollbar {
            display: none;
        }
    }

    .status-tabs-wrap {
        width: max-content;
        /* add right padding so last tab isn't hidden behind the fade */
        margin-right: 28px;
    }

    /* Smaller tabs on mobile so more fit before needing to scroll */
    .status-tab {
        padding: 5px 10px;
        font-size: 13px;

        .tab-count {
            font-size: 11px;
            padding: 2px 5px;
            min-width: 18px;
        }
    }

    /* Filter controls: search full-width, then filters in a row */
    .filter-controls {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr auto;
        gap: 6px;
        width: 100%;
    }

    .search-control {
        width: 100%;
        grid-column: 1 / -1;
    }

    .filter-dropdown-btn--date,
    .filter-dropdown-btn--payment,
    .filter-dropdown-btn--location {
        width: 100%;
        flex: 1;
        min-width: 0;
        justify-content: center;
        gap: 4px;
        padding: 0 8px;

        .filter-btn-label {
            display: none;
        }
    }

    /* Show icon on mobile for payment button */
    .filter-btn-mobile-icon {
        display: block;
    }

    .download-btn span {
        display: none;
    }

    .download-btn {
        padding: 0 10px;
    }

    .date-filter-dropdown {
        max-height: 80vh;
        overflow-y: auto;
    }

    /* Calendar: single-column on mobile */
    .cal-picker {
        width: 100%;
        padding: 16px 16px 0;
        box-sizing: border-box;
    }

    .cal-months {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .cal-footer {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px 0 14px;
    }

    .cal-inputs {
        justify-content: center;
    }

    .cal-footer-actions {
        justify-content: stretch;

        .custom-picker-cancel,
        .custom-picker-apply {
            flex: 1;
            justify-content: center;
        }
    }

    /* Balance card */
    .balance-card-inner {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
        padding: 14px 16px;
    }

    .balance-left {
        width: 100%;
    }

    .balance-divider {
        width: 100%;
        height: 1px;
        margin: 0;
    }

    .balance-middle {
        gap: 20px;
    }

    .balance-actions {
        margin-left: 0;
        justify-content: stretch;

        .btn-pay-balance {
            flex: 1;
            justify-content: center;
        }
    }

    /* No horizontal scroll on mobile — card layout fills full width */
    .table-scroll-wrap {
        overflow-x: visible;
    }

    /* Table footer stacks on very small screens */
    .table-footer {
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
    }
}

/* Empty state */
.orders-empty {
    @apply flex flex-col items-center space-y-2 px-4 py-20;

    p {
        @apply text-base font-semibold text-theme-secondary text-center;
    }

    span {
        @apply w-72 text-center text-sm text-theme-body;

        a {
            @apply border-b border-dashed border-theme-body;
        }
    }
}
</style>
